import { zodResolver } from '@hookform/resolvers/zod'
import { Flex, Text } from '@workwhile/ui'
import { TipAmountInput } from 'components/SendTipModal/components/TipAmountInput'
import { formatCurrency } from 'lib/string'
import { useTipHistory } from 'queries/worker'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { BulkTipVariant } from '../constants'
import { amountSchema } from '../schema'

type BulkTipModalVariantTotalTipProps = {
  shiftId: number
  onSubmit: (data: { tips: { workId: number; amount: number }[] }) => void
}

export const BulkTipModalVariantTotalTip = ({
  shiftId,
  onSubmit,
}: BulkTipModalVariantTotalTipProps) => {
  const form = useForm({
    defaultValues: {
      amount: null,
      workId: null,
    },
    resolver: zodResolver(amountSchema),
  })

  const totalAmount = useWatch({ name: 'amount', control: form.control })

  const { data: works = [] } = useTipHistory(
    { shiftId },
    {
      enabled: Boolean(shiftId),
      select: (data) =>
        data.workHistory.map((item) => ({
          workId: item.work.workId,
        })),
    }
  )

  const handleSubmit = ({ amount }) => {
    const perWorkAmount = amount / works.length
    onSubmit({
      tips: works
        .map((item) => ({
          workId: item.workId,
          amount: perWorkAmount,
        }))
        .filter((item) => item.amount > 0),
    })
  }

  return (
    <FormProvider {...form}>
      <form
        id={BulkTipVariant.TotalTip}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <Flex flexDirection="column" gap={4}>
          <Text>
            Each worker will receive an even split of the total amount.
          </Text>
          <TipAmountInput title="Total Amount" />
          <Flex gap={2} justifyContent="flex-end">
            <Text>{`Per worker (${works.length} worker${
              works.length > 1 ? 's' : ''
            }):`}</Text>

            <Text as="span" fontWeight="bold">
              {totalAmount
                ? formatCurrency(totalAmount / works.length)
                : formatCurrency(0)}
            </Text>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  )
}
